/**
 * 미국 주식 티커 매퍼 — officejs src/services/USTickerMapper 이식 (동일 데이터).
 * 티커(AAPL) 또는 한글 회사명(애플)으로 미국 종목 식별. 데이터 갱신은 officejs
 * 쪽 자동 생성 스크립트 산출물을 그대로 복사한다.
 */

import {
  US_TICKERS,
  US_KO_TO_TICKER,
  US_KO_ORIG_TO_TICKER,
  US_TICKER_TO_KO,
  US_TICKER_TO_EXCHANGE,
} from "./data";
import { US_TICKER_TO_EN, getENSearchIndex } from "./en_data";
import { US_TICKER_TO_CIK } from "./cik_data";

export interface USStockInfo {
  ticker: string;
  nameKo: string;
  exchange: string;
}

/**
 * 티커로 SEC CIK 조회 (EDGAR companyfacts 호출용)
 * 거래소 접두사("NYSE:AXP")나 클래스 접미사가 붙어 있어도 해석한다.
 * @returns 10자리 zero-padded CIK 문자열, 없으면 null
 */
export function getUSCik(ticker: string): string | null {
  if (!ticker) return null;
  const raw = ticker.replace(/^[A-Za-z]+:/, "").toUpperCase().trim();
  const candidates = [raw, raw.replace(/[.\-/].*$/, "")];
  for (const c of candidates) {
    const cik = US_TICKER_TO_CIK[c];
    if (cik) return cik.replace(/^0+/, "").padStart(10, "0");
  }
  return null;
}

/**
 * 주어진 문자열이 미국 티커인지 확인
 */
export function isUSTicker(word: string): boolean {
  return US_TICKERS.has(word.toUpperCase());
}

/**
 * 한글 회사명으로 미국 티커 찾기
 * "애플" → "AAPL", "마이크로소프트" → "MSFT"
 */
export function findUSTickerByKorean(name: string): string | null {
  const noSpace = name.replace(/\s/g, "");

  // 공백 제거 버전에서 검색
  if (US_KO_TO_TICKER[noSpace]) {
    return US_KO_TO_TICKER[noSpace];
  }

  // 원본(공백 포함) 버전에서 검색
  if (US_KO_ORIG_TO_TICKER[name]) {
    return US_KO_ORIG_TO_TICKER[name];
  }

  return null;
}

/**
 * 티커로 종목 정보 조회
 */
export function getUSStockInfo(ticker: string): USStockInfo | null {
  const upper = ticker.toUpperCase();
  if (!US_TICKERS.has(upper)) return null;

  return {
    ticker: upper,
    nameKo: US_TICKER_TO_KO[upper] || upper,
    exchange: US_TICKER_TO_EXCHANGE[upper] || "",
  };
}

/**
 * "NYSE:GS" 같은 거래소 접두사에서 티커를 추출
 * 지원: NYSE, NASDAQ, AMEX, CBOE
 */
const US_EXCHANGE_PREFIX = /^(NYSE|NASDAQ|AMEX|CBOE):(.+)$/i;

function parseExchangePrefixedTicker(word: string): { exchange: string; ticker: string } | null {
  const match = word.match(US_EXCHANGE_PREFIX);
  if (!match) return null;
  return { exchange: match[1].toUpperCase(), ticker: match[2].toUpperCase() };
}

/**
 * 단어 하나에서 미국 티커 resolve
 */
function resolveWord(word: string): USStockInfo | null {
  if (/^\d{4}-\d{1,2}-\d{1,2}/.test(word)) return null;
  if (/^\d+$/.test(word)) return null;

  const prefixed = parseExchangePrefixedTicker(word);
  if (prefixed) {
    return {
      ticker: prefixed.ticker,
      nameKo: US_TICKER_TO_KO[prefixed.ticker] || prefixed.ticker,
      exchange: prefixed.exchange,
    };
  }

  const upper = word.toUpperCase();
  if (US_TICKERS.has(upper)) {
    return {
      ticker: upper,
      nameKo: US_TICKER_TO_KO[upper] || upper,
      exchange: US_TICKER_TO_EXCHANGE[upper] || "",
    };
  }

  if (/[가-힣]/.test(word)) {
    const ticker = findUSTickerByKorean(word);
    if (ticker) {
      return {
        ticker,
        nameKo: US_TICKER_TO_KO[ticker] || ticker,
        exchange: US_TICKER_TO_EXCHANGE[ticker] || "",
      };
    }
  }

  return null;
}

export interface USSearchResult {
  company: { t: string; n: string; c: string; en?: string };
  score: number;
}

/**
 * 자동완성용 검색: 입력 문자열로 미국 종목 검색
 * 티커, 한글명, 영문명 부분 매칭
 * CompanyMatch 호환 포맷으로 반환 (LabPage 쿼리 빌더와 호환)
 */
export function searchUS(query: string, maxResults: number = 8): USSearchResult[] {
  if (!query || query.length < 1) return [];

  const q = query.trim();
  const qUpper = q.toUpperCase();
  const qLower = q.toLowerCase().replace(/[^a-z0-9]/g, "");
  const results: USSearchResult[] = [];
  const seen = new Set<string>();

  function addResult(ticker: string, score: number) {
    if (seen.has(ticker)) return;
    seen.add(ticker);
    const nameKo = US_TICKER_TO_KO[ticker] || ticker;
    const nameEn = US_TICKER_TO_EN[ticker] || "";
    const exchange = US_TICKER_TO_EXCHANGE[ticker] || "";
    const display = nameEn ? `${nameKo} (${ticker})` : `${nameKo} (${ticker})`;
    results.push({
      company: { t: `${exchange}:${ticker}`, n: display, c: ticker, en: nameEn },
      score,
    });
  }

  // 1. 티커 정확 매칭
  if (US_TICKERS.has(qUpper)) {
    addResult(qUpper, 1.0);
  }

  // 2. 티커 prefix 매칭
  if (/^[A-Za-z]/.test(q) && qUpper.length <= 5) {
    for (const ticker of US_TICKERS) {
      if (ticker.startsWith(qUpper) && ticker !== qUpper) {
        addResult(ticker, 0.8);
        if (results.length >= maxResults) break;
      }
    }
  }

  // 3. 영문명 매칭
  if (/^[A-Za-z]/.test(q) && qLower.length >= 2) {
    const index = getENSearchIndex();
    for (const entry of index) {
      if (seen.has(entry.ticker)) continue;
      if (entry.key.startsWith(qLower)) {
        addResult(entry.ticker, 0.85);
        if (results.length >= maxResults) break;
      }
    }
    if (results.length < maxResults) {
      for (const entry of index) {
        if (seen.has(entry.ticker)) continue;
        if (entry.key.includes(qLower)) {
          addResult(entry.ticker, 0.65);
          if (results.length >= maxResults) break;
        }
      }
    }
  }

  // 4. 한글명 부분 매칭
  if (/[가-힣]/.test(q)) {
    for (const [nameKo, ticker] of Object.entries(US_KO_TO_TICKER)) {
      if (seen.has(ticker)) continue;
      if (nameKo.includes(q)) {
        addResult(ticker, nameKo.startsWith(q) ? 0.9 : 0.7);
        if (results.length >= maxResults) break;
      }
    }
  }

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, maxResults);
}

const STOCK_QUERY_KEYWORDS = new Set([
  "주가", "수정주가", "시가", "종가", "고가", "저가", "거래량", "거래대금", "시세",
  "등락률", "등락", "시가총액", "상장주식수",
  "per", "pbr", "roe", "roa", "eps", "bps", "ev/ebitda",
  "매출액", "영업이익", "당기순이익", "재무요약", "재무",
  "뉴스", "공시", "어닝콜", "브리핑", "산업브리핑", "매크로브리핑",
  "stock", "price", "close", "open", "high", "low", "volume", "marketcap",
]);

/**
 * 쿼리 문자열에서 미국 주식 티커를 찾아 반환 (첫 번째 1개)
 */
export function resolveUSTickerFromQuery(query: string): USStockInfo | null {
  const all = resolveAllUSTickersFromQuery(query);
  return all.length > 0 ? all[0] : null;
}

/**
 * 쿼리 문자열에서 미국 주식 티커를 모두 찾아 반환
 * "TSLA AAPL 주가"  → [{ ticker: "TSLA", ... }, { ticker: "AAPL", ... }]
 * "애플 테슬라 주가" → [{ ticker: "AAPL", ... }, { ticker: "TSLA", ... }]
 */
export function resolveAllUSTickersFromQuery(query: string): USStockInfo[] {
  const words = query.trim().split(/\s+/);
  const results: USStockInfo[] = [];
  const seen = new Set<string>();

  for (const word of words) {
    if (STOCK_QUERY_KEYWORDS.has(word.toLowerCase())) continue;

    const info = resolveWord(word);
    if (info && !seen.has(info.ticker)) {
      seen.add(info.ticker);
      results.push(info);
    }
  }

  // 여러 단어를 합쳐서 한글이름 매칭 시도 (예: "아메리칸 에어라인스")
  if (results.length === 0) {
    const koreanWords = words.filter((w) => /[가-힣]/.test(w) && !STOCK_QUERY_KEYWORDS.has(w));
    if (koreanWords.length >= 2) {
      const combined = koreanWords.join("");
      const ticker = findUSTickerByKorean(combined);
      if (ticker) {
        results.push({
          ticker,
          nameKo: US_TICKER_TO_KO[ticker] || ticker,
          exchange: US_TICKER_TO_EXCHANGE[ticker] || "",
        });
      }
    }
  }

  return results;
}
